import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

type Lang = 'en' | 'ar';

const translations = {
  // Header
  'app.title': { en: 'Control Z', ar: 'Control Z' },
  'app.subtitle': { en: 'VISUAL PROMPT ARCHITECT', ar: 'مهندس البرومبت المرئي' },
  'app.live': { en: 'LIVE', ar: 'مباشر' },
  'app.clean': { en: 'Clean', ar: 'مسح' },

  // Toolbar modes
  'mode.basic': { en: 'Basic', ar: 'أساسي' },
  'mode.pro': { en: 'Pro', ar: 'متقدم' },
  'mode.advanced': { en: 'Advanced', ar: 'خبير' },

  // Tools
  'tool.light': { en: 'Light', ar: 'إضاءة' },
  'tool.camera': { en: 'Camera', ar: 'كاميرا' },
  'tool.character': { en: 'Character', ar: 'شخصية' },

  // Formats
  'format.structured': { en: 'Structured', ar: 'منظم' },
  'format.weighted': { en: 'Weighted', ar: 'موزون' },

  // Sidebar
  'sidebar.title': { en: 'Production Suite', ar: 'جناح الإنتاج' },
  'sidebar.noSelection': { en: 'Open controls directly (no manual canvas drop needed)', ar: 'افتح عناصر التحكم مباشرة (بدون سحب يدوي)' },
  'sidebar.clickElement': { en: 'Click an element on the canvas\nto configure its properties', ar: 'اضغط على عنصر في اللوحة\nلتعديل خصائصه' },
  'sidebar.removeElement': { en: 'Remove Element', ar: 'حذف العنصر' },
  'sidebar.colorGrading': { en: 'Color Grading', ar: 'تدريج الألوان' },
  'sidebar.texture': { en: 'Texture & Detail', ar: 'الملمس والتفاصيل' },
  'sidebar.skinBeauty': { en: 'Skin & Beauty', ar: 'البشرة والجمال' },
  'sidebar.vfx': { en: 'VFX', ar: 'مؤثرات بصرية' },
  'sidebar.render': { en: 'Render', ar: 'التصيير' },
  'sidebar.export': { en: 'Export', ar: 'تصدير' },
  'sidebar.composition': { en: 'Composition', ar: 'التكوين' },
  'sidebar.lookGrade': { en: 'LOOK & GRADE', ar: 'المظهر والتدريج' },
  'sidebar.atmosphere': { en: 'ATMOSPHERE', ar: 'الأجواء' },
  'sidebar.output': { en: 'OUTPUT', ar: 'المخرجات' },

  // Element types for sidebar titles
  'element.camera': { en: 'Camera', ar: 'الكاميرا' },
  'element.light': { en: 'Lighting', ar: 'الإضاءة' },
  'element.character': { en: 'Character', ar: 'الشخصية' },

  // Canvas
  'canvas.promptGraph': { en: 'Prompt Graph', ar: 'مخطط البرومبت' },
  'canvas.addElements': { en: 'Add elements from the toolbar', ar: 'أضف عناصر من شريط الأدوات' },
  'canvas.scene': { en: 'Scene', ar: 'المشهد' },
  'canvas.composition': { en: 'Composition', ar: 'التكوين' },

  // Prompt Preview
  'prompt.title': { en: 'Prompt', ar: 'البرومبت' },
  'prompt.fix': { en: 'Fix', ar: 'إصلاح' },
  'prompt.fixing': { en: 'Fixing…', ar: 'جاري الإصلاح…' },
  'prompt.enhance': { en: 'Enhance', ar: 'تحسين' },
  'prompt.enhancing': { en: 'Enhancing…', ar: 'جاري التحسين…' },
  'prompt.physics': { en: 'Physics', ar: 'فيزياء' },
  'prompt.grammar': { en: 'Grammar', ar: 'قواعد' },
  'prompt.expert': { en: 'Expert', ar: 'خبير' },
  'prompt.ready': { en: 'Ready', ar: 'جاهز' },
  'prompt.analyzing': { en: 'Analyzing…', ar: 'جاري التحليل…' },
  'prompt.noIssues': { en: '✓ No active issues', ar: '✓ لا توجد مشاكل' },
  'prompt.issue': { en: 'issue', ar: 'مشكلة' },
  'prompt.issues': { en: 'issues', ar: 'مشاكل' },
  'prompt.zeroConflict': { en: '0-Conflict', ar: '0-تعارض' },
  'prompt.zeroConflictMsg': { en: 'Zero Conflict Mode blocks save/copy until all conflicts are resolved.', ar: 'وضع صفر تعارض يمنع الحفظ/النسخ حتى تحل كل التعارضات.' },
  'prompt.hideIntel': { en: '▾ Hide Intelligence', ar: '▾ إخفاء التحليل' },
  'prompt.showIntel': { en: '▸ Show Intelligence', ar: '▸ عرض التحليل' },
  'prompt.inspector': { en: 'Inspector', ar: 'المفتش' },
  'prompt.autoDirector': { en: 'Auto-Director', ar: 'المخرج التلقائي' },
  'prompt.settingsHistory': { en: 'Settings History', ar: 'سجل الإعدادات' },
  'prompt.noPresets': { en: 'No presets yet', ar: 'لا توجد إعدادات مسبقة' },

  // Intelligence labels
  'intel.subject': { en: 'Subject', ar: 'الموضوع' },
  'intel.action': { en: 'Action', ar: 'الحركة' },
  'intel.environment': { en: 'Environment', ar: 'البيئة' },
  'intel.mood': { en: 'Mood', ar: 'المزاج' },
  'intel.camera': { en: 'Camera', ar: 'الكاميرا' },
  'intel.conflicts': { en: 'Conflicts', ar: 'التعارضات' },
  'intel.score': { en: 'Score', ar: 'النتيجة' },
  'intel.cinematic': { en: 'Cinematic', ar: 'سينمائي' },
  'intel.lens': { en: 'Lens', ar: 'العدسة' },
  'intel.lighting': { en: 'Lighting', ar: 'الإضاءة' },

  // History
  'history.title': { en: 'History', ar: 'السجل' },
  'history.empty': { en: 'Saved prompts & settings\nwill appear here', ar: 'البرومبتات والإعدادات المحفوظة\nستظهر هنا' },
  'history.justNow': { en: 'Just now', ar: 'الآن' },
  'history.mAgo': { en: 'm ago', ar: 'د مضت' },
  'history.hAgo': { en: 'h ago', ar: 'س مضت' },
  'history.savedSettings': { en: 'Saved Settings', ar: 'إعدادات محفوظة' },
  'history.style': { en: 'Style', ar: 'الستايل' },
  'history.palette': { en: 'Palette', ar: 'لوحة الألوان' },
  'history.aspect': { en: 'Aspect', ar: 'النسبة' },
  'history.elements': { en: 'Elements', ar: 'العناصر' },
  'history.loadSettings': { en: 'Load Settings & Edit', ar: 'تحميل الإعدادات والتعديل' },
  'history.copyPrompt': { en: 'Copy prompt', ar: 'نسخ البرومبت' },
  'history.loadPrompt': { en: 'Load prompt & settings', ar: 'تحميل البرومبت والإعدادات' },
  'history.delete': { en: 'Delete', ar: 'حذف' },
  'history.promptCopied': { en: 'Prompt copied!', ar: 'تم نسخ البرومبت!' },

  // Scene Input
  'input.placeholder': { en: 'Describe your scene...', ar: 'وصف المشهد...' },
  'input.uploadRef': { en: 'Upload reference images', ar: 'ارفع صور مرجعية' },
  'input.examples': { en: 'Examples', ar: 'أمثلة' },
  'input.analyzing': { en: 'Analyzing image...', ar: 'جاري تحليل الصورة...' },

  // Palette
  'palette.none': { en: 'None', ar: 'بدون' },
  'palette.createCustom': { en: 'Create Custom Palette', ar: 'إنشاء لوحة مخصصة' },
  'palette.aiRecommend': { en: 'AI Recommend', ar: 'توصية الذكاء' },
  'palette.search': { en: 'Search by name, mood, keyword...', ar: 'بحث بالاسم، المزاج، كلمة مفتاحية...' },
  'palette.paletteName': { en: 'Palette name', ar: 'اسم اللوحة' },
  'palette.promptKeywords': { en: 'Prompt keywords (description)', ar: 'كلمات مفتاحية (وصف)' },
  'palette.saveCustom': { en: 'Save Custom', ar: 'حفظ مخصص' },
  'palette.upTo5': { en: 'Up to 5 colors (min 2 valid HEX)', ar: 'حتى 5 ألوان (الحد الأدنى 2 HEX صالح)' },

  // Misc
  'misc.randomStyle': { en: 'Random Style', ar: 'ستايل عشوائي' },
  'misc.resetAll': { en: 'Reset all settings to defaults', ar: 'إعادة جميع الإعدادات للافتراضي' },
  'misc.followFb': { en: 'Follow us on Facebook', ar: 'تابعنا على فيسبوك' },
} as const;

type TranslationKey = keyof typeof translations;

interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: TranslationKey) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key) => key,
  isRTL: false,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('cz-lang');
    return (saved === 'ar' || saved === 'en') ? saved : 'en';
  });

  useEffect(() => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    localStorage.setItem('cz-lang', lang);
  }, [lang]);

  const toggleLang = () => setLang(prev => prev === 'en' ? 'ar' : 'en');

  const t = useCallback((key: TranslationKey): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry.en;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, isRTL: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
